/* eslint-disable no-unused-vars */
import axios from "axios";
import ApiEndpoints, { API_KEY, ApiName, JWT_TOKEN, MIDDLEWARE, NON_MIDDLEWARE } from "./ApiEndpoints";
import Cookies from "js-cookie";
import { getCookie, setCookie } from "../utils/CommonFunctions";
import { encryptCryptoJson, decryptCrypto, jwtApiKey, getClientId } from "@ipru-common/encryptions/aes-encryption";
import { FAILED, SUCCESS } from "../constants/SiteConstants";
import appConfig from "@ipru-common/config/environment";

export const TRACER_SOURCE_ID =
  "ea0403e8f3b40e3211d51907ee489b79547bdd1dae6e491556de5956e77b149be974472b6c75543d548cb4d69f85489c";
export const ACCESS_TOKEN_SOURCE_ID = "edf5417f57313de55c69bd83004ecd856fa443fcd334f43b68be851694698f3b";
export const ADHAR_AUTH_TOKEN = "OHQ2dHNOS29jRUNjNHpiUE9XVzRudz09";

export const TOKENS = {
  JWT: `JWT`,
  JWT_NON_MIDDLEWARE: `JWT_NON_MIDDLEWARE`,
  AUTH: "authToken",
  ACCESS_TOKEN: "ACCESS_TOKEN",
  REFRESH_ACCESS_TOKEN: "REFRESH_ACCESS_TOKEN",
  SPOUSE_ACCESS_TOKEN: "SPOUSE_ACCESS_TOKEN",
  REFRESH_SPOUSE_ACCESS_TOKEN: "REFRESH_SPOUSE_ACCESS_TOKEN",
};

let isTokenBeingFetched = {
  [MIDDLEWARE]: false,
  [NON_MIDDLEWARE]: false,
};

let isAuthTokenBeingFetched = false;
let requestQueue = { [MIDDLEWARE]: [], [NON_MIDDLEWARE]: [] };

const jwtTokeName = {
  [MIDDLEWARE]: TOKENS.JWT,
  [NON_MIDDLEWARE]: TOKENS.JWT_NON_MIDDLEWARE,
};

const getJwtToken = (middlewareType) => getCookie(jwtTokeName[middlewareType]);

const setJwtToken = (token, middlewareType) => setCookie(jwtTokeName[middlewareType], token, 1 / 96);

const getAuthToken = () => Cookies.get(TOKENS.AUTH);

const setAuthToken = (token) => setCookie(TOKENS.AUTH, token, 1 / 96);

const executeQueuedRequests = (middlewareType) => {
  // eslint-disable-next-line no-prototype-builtins
  if (requestQueue?.[middlewareType]) {
    // Loop through the requests for the current middleware type
    while (requestQueue[middlewareType].length > 0) {
      const { resolve, reject, args } = requestQueue[middlewareType].shift();

      // Call service function with arguments and handle promises
      service({ ...args, isQueueCall: true })
        .then(resolve)
        .catch(reject);
    }
    // Clear the queue after processing
    requestQueue[middlewareType] = [];
  }
};

const fetchJwtToken = async (MIDDLEWARE_TYPE) => {

  if (!isTokenBeingFetched[MIDDLEWARE_TYPE]) {
    isTokenBeingFetched[MIDDLEWARE_TYPE] = true;
    const { endpoint, headers = {} } = ApiEndpoints.JWT_TOKEN;
    const config = await appConfig();
    const { BASE } = config.API.JWT_TOKEN;
    try {
      const response = await axios.post(
        `${BASE}${endpoint}`,
        {},
        {
          headers: {
            Authorization: `Basic ${jwtApiKey[MIDDLEWARE_TYPE]}`,
            ...headers,
          },
        }
      );

      if (response?.data?.token) {
        setJwtToken(response.data.token, MIDDLEWARE_TYPE);

        const jwtToken = response.data.token; // Assuming the token is in response.data.jwtToken
        if (MIDDLEWARE_TYPE === MIDDLEWARE) {
          isAuthTokenBeingFetched = true;
          fetchAuthToken(jwtToken, MIDDLEWARE_TYPE);
        }
      } else {
        throw "Error fetching JWT Token:";
      }
    } catch (error) {
      // Handle errors appropriately
      console.error("Error fetching JWT Token:", error);
      throw error;
    } finally {
      if (!isAuthTokenBeingFetched) {
        isTokenBeingFetched[MIDDLEWARE_TYPE] = false;
        executeQueuedRequests(MIDDLEWARE_TYPE);
      }
    }
  }
};

const fetchAuthToken = async (jwt, middlewareType) => {
  isTokenBeingFetched[middlewareType] = true;
  try {
    const { endpoint } = ApiEndpoints.AUTH_TOKEN;
    const config = await appConfig();
    const BASE = config.API.AUTH_TOKEN.BASE;
    const toEncrypt = config.API.AUTH_TOKEN.encryption;
    let data = { appCode: "BOLTECH" };
    if (toEncrypt) data = { encryptedRequest: await encryptCryptoJson(data, middlewareType) };
    // Make a request to fetch the Auth Token using the JWT
    let response = await axios.post(`${BASE}${endpoint}`, data, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    if (toEncrypt && response?.data?.encryptedResponse) {
      const decrypted = await decryptCrypto(response.data.encryptedResponse, middlewareType);
      try {
        response.data = JSON.parse(decrypted);
      } catch (err) {
        console.error("Error parsing response:", err);
        response.data = decrypted;
      }
    }
    const authToken = response.data.token;

    setAuthToken(authToken);

    // return the authToken
    return authToken;
  } catch (error) {
    // Handle errors appropriately
    console.error("Error fetching Auth Token:", error);
    throw error;
  } finally {
    isTokenBeingFetched[middlewareType] = false;
    executeQueuedRequests(middlewareType);
  }
};

// "secretKey": "9000000001",
// "secretPass": "23081991"
// For the dropoff
export const fetchAccessToken = async ({ mobile, dob, tokenName }, refresh = false, REFRESH_TOKEN = "") => {
  const tokenKey = tokenName || TOKENS.ACCESS_TOKEN;
  const refreshTokenKey = tokenName ? `REFRESH_${tokenName}` : TOKENS.REFRESH_ACCESS_TOKEN;

  const config = await appConfig();
  const MIDDLEWARE_TYPE = config.API.ACCESS_TOKEN.MIDDLEWARE;

  if (MIDDLEWARE_TYPE === MIDDLEWARE) {
    try {
      const token = getCookie(tokenKey);
      if (token && token !== "undefined") {
        return new Promise((resolve) => {
          resolve(token);
        });
      }
      const params = {
        apiName: ApiName.ACCESS_TOKEN,
        method: "POST",
        data: {
          source: ACCESS_TOKEN_SOURCE_ID,
          appName: "BOLDigiDrive",
          reqType: "SECURITY",
          reqSubType: "USER",
          ...(!refresh
            ? { secretKey: mobile, secretPass: dob }
            : { refresh_token: REFRESH_TOKEN || getCookie(refreshTokenKey) }),
        },
      };

      return new Promise((resolve) => {
        // const processId = Date.now();
        // if (loader) startLoader(processId);
        // setIsLoading(true);
        service(params)
          .then((result) => {
            console.log(result, "result accessToken");
            const accessToken = result.result.access_token;
            const refreshToken = result.result.access_token;
            const expIn = 1 / ((result.result.expires_in / 60) * 24);

            setCookie(tokenKey, accessToken, expIn);
            setCookie(refreshTokenKey, refreshToken, expIn);
            resolve(accessToken);
          })
          .catch((error) => {
            resolve({
              status: FAILED,
              result: error,
            });
            console.log(error, "error ");
          });
      });
    } catch (error) {
      // Handle errors appropriately
      console.error("Error fetching Access Token:", error);
      throw error;
    }
  } else {
    try {
      const token = getCookie(tokenKey);
      if (token && token !== "undefined") {
        return token;
      }
      const { endpoint, headers } = ApiEndpoints.ACCESS_TOKEN[MIDDLEWARE_TYPE];
      const { BASE } = config.API.ACCESS_TOKEN;

      // Make a request to fetch the Auth Token using the JWT
      const response = await axios.post(
        `${BASE}${endpoint}`,
        {
          source: ACCESS_TOKEN_SOURCE_ID,
          appName: "BOLDigiDrive",
          reqType: "SECURITY",
          reqSubType: "USER",
          ...(!refresh
            ? { secretKey: mobile, secretPass: dob }
            : { refresh_token: REFRESH_TOKEN || getCookie(refreshTokenKey) }),
        },
        {
          headers,
        }
      );

      const accessToken = response.data.access_token;
      const refreshToken = response.data.access_token;
      const expIn = 1 / ((response.data.expires_in / 60) * 24);

      setCookie(tokenKey, accessToken, expIn);
      setCookie(refreshTokenKey, refreshToken, expIn);

      // return the authToken
      return accessToken;
    } catch (error) {
      // Handle errors appropriately
      console.error("Error fetching Access Token:", error);
      throw error;
    }
  }
};

const enqueueRequest = (args, MIDDLEWARE_TYPE) => {
  return new Promise((resolve, reject) => {
    requestQueue[MIDDLEWARE_TYPE].push({ resolve, reject, args });
  });
};

const service = async ({
  apiName,
  method = "GET",
  data = null,
  queryParams,
  headers = {},
  isQueueCall = false,
  abort = null,
  directResponse = false,
  isPayloadEncrypted = false,
  safeBase64Encoding = false,
}) => {
  const config = await appConfig();
  const MIDDLEWARE_TYPE = config.API[apiName].MIDDLEWARE;
  method = MIDDLEWARE_TYPE === MIDDLEWARE ? "POST" : method
  const BASE = config.API[apiName].BASE;
  const toEncrypt = config.API[apiName]?.encryption;
  let jwtToken = getJwtToken(MIDDLEWARE_TYPE);
  let authToken = getAuthToken();

  if (!isPayloadEncrypted && toEncrypt && !(data && typeof data === "object" && "encryptedRequest" in data)) {
    data = {
      encryptedRequest: await encryptCryptoJson(data, MIDDLEWARE_TYPE, safeBase64Encoding),
    };
    isPayloadEncrypted = true;
  }

  let requestData = data;
  let requestParams = queryParams;
  const isMiddlewareApi = MIDDLEWARE_TYPE === MIDDLEWARE;
  const effectiveMethod = isMiddlewareApi ? "POST" : (method || "GET").toUpperCase();

  const { endpoint, authType, noAuth = false, CUSTOM_API_KEY = "" } = ApiEndpoints[apiName][MIDDLEWARE_TYPE];

  if (!noAuth) {
    if (isQueueCall) {
      if (authType !== API_KEY && !jwtToken) {
        return Promise.reject("JWT Token not available");
      }
      if (!authToken && MIDDLEWARE_TYPE === MIDDLEWARE) {
        return Promise.reject("Auth Token not available");
      }
    }

    if (!jwtToken && !isTokenBeingFetched[MIDDLEWARE_TYPE] && authType !== API_KEY) {
      await fetchJwtToken(MIDDLEWARE_TYPE);
      jwtToken = getJwtToken(MIDDLEWARE_TYPE);

      authToken = getAuthToken();
    }

    if (
      jwtToken &&
      !authToken &&
      !isTokenBeingFetched[MIDDLEWARE_TYPE] &&
      authType !== API_KEY &&
      MIDDLEWARE_TYPE === MIDDLEWARE
    ) {
      await fetchAuthToken(jwtToken, MIDDLEWARE_TYPE);
      authToken = getAuthToken();
    }

    if (
      (isTokenBeingFetched[MIDDLEWARE_TYPE] && MIDDLEWARE_TYPE === MIDDLEWARE) ||
      (isTokenBeingFetched[MIDDLEWARE_TYPE] && !jwtToken && authType === JWT_TOKEN)
    ) {
      // If tokens are not available, enqueue the request
      return enqueueRequest({ apiName, method, data, directResponse, isPayloadEncrypted, safeBase64Encoding }, MIDDLEWARE_TYPE);
    }

    if (isMiddlewareApi) {
      headers = {
        Authorization: `Bearer ${jwtToken}`,
        Token: authToken, // Custom header for Auth Token
      };
    } else {
      if (authType === JWT_TOKEN) {
        headers["Authorization"] = `Bearer ${jwtToken}`;
      } else if (authType === API_KEY) {
        headers["x-api-key"] = CUSTOM_API_KEY || getClientId(NON_MIDDLEWARE); // Use actual API key
      }
    }
  }

  if (isMiddlewareApi) {
    // If developer passed payload as query params but didn't provide body, move it to data
    if (!requestData && requestParams) {
      requestData = requestParams;
    }
    // If both are present we still set requestParams to undefined.
    requestParams = undefined;
  }

  let controller = null;

  if (abort) {
    // If abort parameter is passed, create an AbortController
    controller = new AbortController();
  }

  try {
    if (abort && controller) {
      // Abort the request after the specified timeout in seconds
      setTimeout(() => {
        console.log("test");
        controller.abort(); // Abort the request
      }, abort * 1000); // Convert seconds to milliseconds
    }

    let response = await axios({
      method: effectiveMethod,
      url: `${BASE}${endpoint}`,
      params: requestParams,
      data: requestData,
      headers,
      withCredentials: MIDDLEWARE_TYPE === MIDDLEWARE,
      ...(abort ? { signal: controller.signal } : {}),
    });
    if (toEncrypt && response?.data?.encryptedResponse) {
      const decrypted = await decryptCrypto(response.data.encryptedResponse, MIDDLEWARE_TYPE);
      try {
        response.data = JSON.parse(decrypted);
      } catch (err) {
        console.error("Error parsing response:", err);
        response.data = decrypted;
      }
    }
    return directResponse ? response : handleResponse(response);
  } catch (error) {
    return Promise.reject(error);
  }
};

const handleResponse = (response) => {
  if (response.status <= 300) {
    return { statusCode: response.status, status: SUCCESS, result: response.data };
  } else {
    return {
      statusCode: response.status,
      status: FAILED,
      result: { message: "Request failed with status: " + response.status },
    };
  }
};

// Function to get the client's IP address
export async function getIpAddress() {
  try {
    const response = await fetch("https://httpbin.org/ip");
    const data = await response.json();
    return data.origin;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return null;
  }
}

export default service;
